import { X } from 'lucide-react';
import { type FormEvent, type ReactNode, useCallback, useEffect, useId, useLayoutEffect, useRef, useState } from 'react';
import { cn } from '../../lib/cn';
import { isKeyboardModality } from '../../lib/inputModality';
import { Button } from './Button';
import { useEscapeLayer } from './escape';
import { LayerProvider } from './layer';
import { nextLayerOrder } from './Popover';

type DialogProps = {
  open: boolean;
  onClose: () => void;
  title: ReactNode;
  description?: ReactNode;
  children?: ReactNode;
  footer?: ReactNode;
  size?: 'sm' | 'md' | 'lg' | 'xl';
  /** Обгорнути вміст формою: Enter / Ctrl+Enter надсилає. */
  onSubmit?: (e: FormEvent<HTMLFormElement>) => void;
  /** Не закривати кліком по тлу (форми з введеними даними). */
  dismissible?: boolean;
  className?: string;
  bodyClassName?: string;
  initialFocusSelector?: string;
};

const SIZES = { sm: 'sm:max-w-[420px]', md: 'sm:max-w-[560px]', lg: 'sm:max-w-[720px]', xl: 'sm:max-w-[920px]' };

/**
 * Діалог на основі нативного <dialog>: верхній шар, інертне тло, повернення фокуса.
 * На телефоні — нижній аркуш на всю ширину.
 */
export function Dialog({
  open,
  onClose,
  title,
  description,
  children,
  footer,
  size = 'md',
  onSubmit,
  dismissible = true,
  className,
  bodyClassName,
  initialFocusSelector,
}: DialogProps) {
  const ref = useRef<HTMLDialogElement | null>(null);
  const [container, setContainer] = useState<HTMLDialogElement | null>(null);
  const [mounted, setMounted] = useState(open);
  const [closing, setClosing] = useState(false);
  const [order, setOrder] = useState(0);
  const titleId = useId();
  const descId = useId();
  const restoreRef = useRef<HTMLElement | null>(null);
  const formRef = useRef<HTMLFormElement>(null);
  const setDialogRef = useCallback((el: HTMLDialogElement | null) => {
    ref.current = el;
    setContainer(el);
  }, []);

  useEffect(() => {
    if (open) {
      setMounted(true);
      setClosing(false);
    } else if (mounted) {
      setClosing(true);
      // Без анімації виходу — за зменшеного руху й коли діалог закрито з клавіатури (Esc, Ctrl+Enter).
      const instant = window.matchMedia('(prefers-reduced-motion: reduce)').matches || isKeyboardModality();
      const t = window.setTimeout(() => {
        setClosing(false);
        setMounted(false);
      }, instant ? 0 : 140);
      return () => window.clearTimeout(t);
    }
    return undefined;
  }, [open, mounted]);

  useLayoutEffect(() => {
    const el = ref.current;
    if (!el || !mounted) return;
    if (!el.open) {
      restoreRef.current = document.activeElement as HTMLElement | null;
      setOrder(nextLayerOrder());
      el.showModal();
      const target =
        (initialFocusSelector && el.querySelector<HTMLElement>(initialFocusSelector)) ||
        el.querySelector<HTMLElement>('[data-autofocus]') ||
        el.querySelector<HTMLElement>('input:not([type=hidden]):not([disabled]), textarea:not([disabled]), select:not([disabled])');
      if (target) target.focus({ preventScroll: true });
    }
  }, [mounted, initialFocusSelector]);

  useEffect(() => {
    if (mounted) return;
    const restore = restoreRef.current;
    if (restore && restore.isConnected && (document.activeElement === document.body || !document.activeElement)) restore.focus({ preventScroll: true });
  }, [mounted]);

  useEscapeLayer(open, onClose);

  if (!mounted) return null;

  const body = (
    <>
      <header className="flex items-start gap-3 border-b border-line px-5 pt-4 pb-3">
        <div className="min-w-0 flex-1">
          <h2 id={titleId} className="text-title font-semibold text-fg">
            {title}
          </h2>
          {description && (
            <p id={descId} className="mt-0.5 text-meta text-fg-2">
              {description}
            </p>
          )}
        </div>
        <button
          type="button"
          onClick={onClose}
          aria-label="Закрити"
          className="-mt-0.5 -mr-1.5 inline-flex size-8 shrink-0 items-center justify-center rounded-sm text-fg-3 hover:bg-surface-2 hover:text-fg"
        >
          <X className="size-4" aria-hidden />
        </button>
      </header>
      {/* flex-auto, а не flex-1: діалог має висоту за вмістом (h-fit), і Safari (WebKit) рахує основу 0% як нуль —
          уміст стискався до смужки. З основою auto вміст дає висоту діалогу, а при max-height тіло прокручується. */}
      <div className={cn('min-h-0 flex-auto overflow-y-auto overscroll-contain px-5 py-4', bodyClassName)}>{children}</div>
      {footer && (
        <footer className="flex flex-wrap items-center justify-end gap-2 border-t border-line bg-canvas/60 px-5 py-3 pb-[max(12px,env(safe-area-inset-bottom))]">
          {footer}
        </footer>
      )}
    </>
  );

  return (
    <dialog
      ref={setDialogRef}
      data-ers-layer=""
      data-ers-order={order}
      data-closing={closing ? '' : undefined}
      aria-labelledby={titleId}
      aria-describedby={description ? descId : undefined}
      onCancel={(e) => {
        // Esc обробляє стек шарів (закривається лише верхній шар).
        e.preventDefault();
      }}
      onKeyDown={(e) => {
        // Ctrl/Cmd+Enter надсилає форму, де б не був фокус у діалозі.
        if (onSubmit && e.key === 'Enter' && (e.metaKey || e.ctrlKey)) {
          e.preventDefault();
          formRef.current?.requestSubmit();
        }
      }}
      onClose={() => {
        if (open) onClose();
      }}
      onPointerDown={(e) => {
        if (dismissible && e.target === ref.current) {
          const r = ref.current.getBoundingClientRect();
          const inside = e.clientX >= r.left && e.clientX <= r.right && e.clientY >= r.top && e.clientY <= r.bottom;
          if (!inside) onClose();
        }
      }}
      className={cn(
        'ers-dialog m-0 flex max-h-[min(92dvh,900px)] w-full max-w-none flex-col overflow-hidden bg-surface p-0 text-fg shadow-pop',
        'fixed inset-x-0 top-auto bottom-0 rounded-t-lg border-t border-line',
        // Від sm — по центру з полями 16 px: широкий діалог (xl = 920 px) на планшеті не впирається в краї екрана.
        'sm:inset-0 sm:m-auto sm:h-fit sm:w-[calc(100%-2rem)] sm:rounded-lg sm:border',
        SIZES[size],
        className,
      )}
    >
      <LayerProvider container={container}>
        {onSubmit ? (
          <form
            ref={formRef}
            noValidate
            className="flex min-h-0 flex-auto flex-col"
            onSubmit={(e) => {
              e.preventDefault();
              onSubmit(e);
            }}
          >
            {body}
          </form>
        ) : (
          body
        )}
      </LayerProvider>
    </dialog>
  );
}

/** Підтвердження дії. Для незворотних дій — tone="danger". */
export function ConfirmDialog({
  open,
  onClose,
  onConfirm,
  title,
  description,
  confirmLabel,
  tone = 'primary',
  loading,
  children,
}: {
  open: boolean;
  onClose: () => void;
  onConfirm: () => void;
  title: ReactNode;
  description?: ReactNode;
  confirmLabel: string;
  tone?: 'primary' | 'danger';
  loading?: boolean;
  children?: ReactNode;
}) {
  return (
    <Dialog
      open={open}
      onClose={onClose}
      title={title}
      size="sm"
      onSubmit={() => onConfirm()}
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            Скасувати
          </Button>
          <Button type="submit" variant={tone === 'danger' ? 'danger' : 'primary'} loading={loading} data-autofocus>
            {confirmLabel}
          </Button>
        </>
      }
    >
      {description && <p className="text-body text-fg-2">{description}</p>}
      {children}
    </Dialog>
  );
}
